/**
 * TypeScript wrapper for the WASM skin-mesh generator.
 *
 * Preserves the `SkinInput` / `SkinOutput` shapes the skin modifier core
 * already consumes at `viewport/modifiers/skinCore.ts`, so call sites don't
 * change. Numeric style/cap enums cross the WASM boundary; the TS side
 * maps the human-readable strings to codes.
 */

import {
  asSharedTriangleIndices,
  type SharedTriangleIndices,
} from "../modeling/types.ts";
import { generate_skin_mesh as wasmGenerateSkinMesh } from "./pkg/poseref_wasm.js";

export type SkinStyle = "smooth" | "cable" | "ribbed" | "bundle" | "chain";
export type SkinCapStyle = "flat" | "ferrule" | "none";

export interface SkinInput {
  positions: Float32Array;
  edges: Uint32Array;
  radii: Float32Array;
  subdivisions: number;
  fillCaps: boolean;
  capStyle?: SkinCapStyle;
  preserveVolume?: boolean;
  style?: SkinStyle;
  strandCount?: number;
  twistRate?: number;
  bundleJitter?: number;
  ribFrequency?: number;
  ribDepth?: number;
  // --- Chain style (absolute meters / radians) ---
  linkLength?: number;
  linkAspect?: number;
  wireThickness?: number;
  linkOverlap?: number;
  /** Twist between adjacent links around the path tangent, in radians. */
  twistPerLink?: number;
  linkSegments?: number;
}

export interface SkinOutput {
  positions: Float32Array;
  indices: SharedTriangleIndices;
  normals: Float32Array;
  sourceVerts: Int32Array;
}

const STYLE_CODE: Record<SkinStyle, number> = {
  smooth: 0,
  cable: 1,
  ribbed: 2,
  bundle: 3,
  chain: 4,
};

const CAP_CODE: Record<SkinCapStyle, number> = {
  flat: 0,
  ferrule: 1,
  none: 2,
};

export function generateSkinMesh(input: SkinInput): SkinOutput {
  const capStyle: SkinCapStyle =
    input.capStyle ?? (input.fillCaps ? "flat" : "none");
  const style: SkinStyle = input.style ?? "smooth";

  const result = wasmGenerateSkinMesh(
    input.positions,
    input.edges,
    input.radii,
    input.subdivisions,
    CAP_CODE[capStyle],
    input.preserveVolume ?? false,
    STYLE_CODE[style],
    input.strandCount ?? 3,
    input.twistRate ?? 3,
    input.bundleJitter ?? 0.3,
    input.ribFrequency ?? 8,
    input.ribDepth ?? 0.5,
    input.linkLength ?? 0.04,
    input.linkAspect ?? 0.6,
    input.wireThickness ?? 0.005,
    input.linkOverlap ?? 0.4,
    input.twistPerLink ?? Math.PI / 2,
    input.linkSegments ?? 24,
  );

  const output: SkinOutput = {
    positions: result.positions,
    indices: asSharedTriangleIndices(result.indices),
    normals: result.normals,
    sourceVerts: result.source_verts,
  };
  result.free();
  return output;
}
